<script setup lang="ts">
import { VWorkspaceContext } from "@app/core";
import type { WorkspaceContextItem, WorkspaceContextUser } from "@app/core";
import { useQuery } from "@vue/apollo-composable";
import { computed } from "vue";
import { useRouter } from "vue-router";

import { GET_ORGANIZATION } from "../api/organization.gql";
import { useSwitchOrganization } from "../composables/useSwitchOrganization";

import { RouteNames } from "@/app/router/types/names";
import { useAuthStore } from "@/features/auth";
import { useAppVersionQuery } from "@/shared/api/app";
import { useUserStore } from "@/shared/stores/useUserStore";

const { collapsed = false } = defineProps<{ collapsed?: boolean }>();

const router = useRouter();
const userStore = useUserStore();
const authStore = useAuthStore();

// ── Org data ──────────────────────────────────────────────────────────────────
// SuperAdmins switch orgs from the Organizations tab; this block only reflects
// the currently active organization.
const orgs = computed(() => userStore.user?.orgPermissions ?? []);
const activeOrgId = computed(() => userStore.user?.orgId ?? null);
const activeOrg = computed(
  () => orgs.value.find(o => o.orgId === activeOrgId.value) ?? orgs.value[0],
);

const items = computed<WorkspaceContextItem[]>(() =>
  orgs.value.map(o => ({ id: o.orgId, label: o.orgName })),
);

// Authoritative active-org name: `orgPermissions` only covers orgs the user is a member
// of, but a SuperAdmin can switch into any org. Fetch the active org by id (backend allows
// ADMIN any org, others only their own) and fall back to the membership lookup on load/null.
const { result: activeOrgResult } = useQuery(
  GET_ORGANIZATION,
  () => ({ organizationId: activeOrgId.value ?? "" }),
  () => ({ enabled: !!activeOrgId.value }),
);
const activeOrgName = computed(
  () => activeOrgResult.value?.organization?.name ?? activeOrg.value?.orgName ?? "",
);

// ── Org switching ───────────────────────────────────────────────────────────
// Any member of >1 org (incl. regular USERs) can switch their active org here. The
// backend's switchOrg authorises non-admins by active membership, and `orgs` only ever
// lists orgs the user actually belongs to — so they can never pick one they can't enter.
const { switchOrg, switching } = useSwitchOrganization();

const contextUser = computed<WorkspaceContextUser | null>(() => {
  const user = userStore.user;
  return user ? { name: user.name, email: user.email, role: user.role } : null;
});

// ── Handlers ──────────────────────────────────────────────────────────────────
const goToOrgPage = () => {
  router.push({ name: RouteNames.ORGANIZATION });
};

const goToProfile = () => {
  router.push({ name: RouteNames.MY_PROFILE });
};

// ── Versions ──────────────────────────────────────────────────────────────────

const { result: versionResult } = useAppVersionQuery();
const apiVersion = computed(() => versionResult.value?.appVersion ?? null);
const uiVersion = __UI_VERSION__;
</script>

<template>
  <VWorkspaceContext
    :active-id="activeOrgId"
    :active-label="activeOrgName"
    :collapsed
    :items
    :switching
    :user="contextUser"
    caption="Organization"
    head-clickable
    switch-heading="Switch organization"
    @head-click="goToOrgPage"
    @logout="authStore.logout()"
    @profile="goToProfile"
    @switch="switchOrg"
  >
    <template #menu-footer>
      <div class="version-line">
        UI v{{ uiVersion }} · API v{{ apiVersion ?? "…" }}
      </div>
    </template>
  </VWorkspaceContext>
</template>

<style scoped>
.version-line {
  margin-top: 0.25rem;
  padding: 0.375rem;
  border-top: 1px solid rgb(var(--color-base-300));
  color: rgb(var(--color-secondaryText));
  font-size: 0.6875rem;
  text-align: center;
}
</style>
